import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FaExternalLinkAlt, FaLock, FaPlaneDeparture, FaUsers } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useHistory, useLocation } from 'react-router-dom';
import CabecalhoEventoAereo from '../../components/CabecalhoEventoAereo';
import ConfirmarAcaoModal from '../../components/ConfirmarAcaoModal';
import { FinCheck, FinInput } from '../../components/FinField';
import ErpTemplate from '../../components/ErpTemplate';
import PlanilhaMalhaAerea from '../../components/PlanilhaMalhaAerea';
import api from '../../services/api';
import notify from '../../services/notify';
import { numerarPorGrupo, acoesDeImportacao, selecaoDeImportacao, contarLadosMarcados, LADOS_IMPORTACAO, lerSelecaoDaMalha, guardarSelecaoDaMalha } from '../../util/malhaAereo';
import '../../css/financeiro-parcela-pagar.css';

/**
 * Importação das Opções de Aéreo — o RSVP leva as opções das malhas APROVADAS para as opções
 * que o participante escolhe (`eventoaereo`), e retira quando quiser. A rota e o back mantêm o
 * nome antigo, Portação.
 *
 * Cada malha importável — aprovada, ou em alteração, que mantém a aprovação de Eventos — aparece
 * na mesma planilha da tela da malha, com os campos só para
 * leitura: na coluna "Importar" se marca a ida e a volta de cada opção. As opções já no evento
 * e as manuais têm aba própria. A origem de cada opção do evento é fixa — da malha, manual ou
 * alterada —, então não existe vincular nem desvincular. Toda regra mora no back
 * (`EventoAereoPortacaoService`): a tela mostra o estado que ele calcula e manda a lista de ações.
 */

const ESTADO = {
    nova:       { rotulo: 'Nova', cor: 'is-info', dica: 'Ainda não está nas opções do evento.' },
    duplicata:  { rotulo: 'Manual igual', cor: 'is-warn', dica: 'Não veio desta malha, mas o evento já tem uma opção manual com o mesmo voo.' },
    importada:  { rotulo: 'Importada', cor: 'is-ok', dica: 'Está nas opções do evento, igual à malha.' },
    parcial:    { rotulo: 'Parcial', cor: 'is-info', dica: 'Está no evento com um lado só; o outro lado existe na malha e ainda não foi importado.' },
    divergente: { rotulo: 'Diverge da malha', cor: 'is-warn', dica: 'Está no evento, mas algum campo mudou na malha depois da importação.' },
    alterada:   { rotulo: 'Alterada', cor: 'is-warn', dica: 'Alterada na tela de opções: não volta a ser da malha. Continua no evento.' },
};

const ROTULO_CAMPO = {
    aeroportoOrigem: 'Cidade do participante',
    idadata: 'Data da ida', idacompanhia: 'Cia da ida', idavoo: 'Voo da ida',
    idaorigem: 'Origem da ida', idadestino: 'Destino da ida', idahorasaida: 'Saída da ida', idahorachegada: 'Chegada da ida',
    retornodata: 'Data do retorno', retornocompanhia: 'Cia do retorno', retornovoo: 'Voo do retorno',
    retornoorigem: 'Origem do retorno', retornodestino: 'Destino do retorno',
    retornohorasaida: 'Saída do retorno', retornohorachegada: 'Chegada do retorno',
};

const CAMPOS_LADO = {
    ida: { data: 'idadata', cia: 'idacompanhia', voo: 'idavoo', origem: 'idaorigem', destino: 'idadestino', saida: 'idahorasaida', chegada: 'idahorachegada' },
    retorno: { data: 'retornodata', cia: 'retornocompanhia', voo: 'retornovoo', origem: 'retornoorigem', destino: 'retornodestino', saida: 'retornohorasaida', chegada: 'retornohorachegada' },
};

const ABAS = [
    { key: 'malhas', label: 'Malhas' },
    { key: 'noEvento', label: 'No evento' },
    { key: 'manuais', label: 'Opções manuais' },
];

const noEvento = (linha) => (linha.tipo === 'semOrigem' ? linha.r : linha.o.eventoAereo);
const podeRetirar = (linha) => !!noEvento(linha) && !noEvento(linha).travada;
// As diferenças de voo vêm por lado; a comparação mostra as dos dois.
const diferencasDe = (o) => LADOS_IMPORTACAO.flatMap(lado => o.lados?.[lado]?.diferencas ?? []);

const dataCurta = (iso) => {
    if (!iso) return '—';
    const d = new Date(String(iso).replace(' ', 'T'));
    return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('pt-BR');
};

// Um voo numa célula: cada perna da conexão numa linha (" | " é o separador gravado).
function Voo({ campos, lado }) {
    const chaves = CAMPOS_LADO[lado];
    const partes = {};
    Object.entries(chaves).forEach(([nome, campo]) => {
        partes[nome] = String(campos?.[campo] ?? '').split('|').map(s => s.trim());
    });
    const n = Math.max(0, ...Object.values(partes).map(a => (a.some(Boolean) ? a.length : 0)));
    if (!n) return <span className="fin-pp__cell-muted">—</span>;
    return (
        <div className="fin-pp__voo">
            {Array.from({ length: n }).map((_, i) => (
                <div key={i}>
                    <div className="fin-pp__voo-rota">{partes.origem[i] || '?'} → {partes.destino[i] || '?'}</div>
                    <div className="fin-pp__voo-info">
                        {[
                            partes.data[i],
                            [partes.cia[i], partes.voo[i]].filter(Boolean).join(' '),
                            [partes.saida[i], partes.chegada[i]].filter(Boolean).join('–'),
                        ].filter(Boolean).join(' · ') || '—'}
                    </div>
                </div>
            ))}
        </div>
    );
}

// Uma escolha por participante: quem pegou esta opção na ida e no retorno conta uma vez.
function Escolhas({ opcao }) {
    if (!opcao) return <span className="fin-pp__cell-muted">—</span>;
    const total = opcao.escolhas?.participantes ?? 0;
    if (!total) return <span className="fin-pp__cell-muted">0</span>;
    return (
        <span className="fin-pp__pill is-warn"
            title={`${total} ${total !== 1 ? 'participantes' : 'participante'} (ida: ${opcao.escolhas.ida} · retorno: ${opcao.escolhas.retorno}). Escolhida: não sai nem muda de voo. Para liberar, resete a etapa de aéreo de quem escolheu.`}>
            <FaLock size={9} />
            {total}
        </span>
    );
}

// Para quem a opção foi liberada, além do evento todo — e se é só deles. O número abre o modal
// de retirar dos participantes.
function Portados({ opcao, disabled, onAbrir }) {
    if (!opcao) return null;
    return (
        <>
            {opcao.somenteVinculados && (
                <span className="fin-pp__pill is-info" title="Não é do evento todo: só os participantes vinculados veem esta opção, no cadastro de cada um. Não aparece nas opções do evento.">
                    Só para participantes liberados
                </span>
            )}
            {opcao.portados > 0 && (
                <button type="button" className="fin-pp__linkbtn" disabled={disabled}
                    title="Ver os participantes para quem a opção foi liberada e retirar"
                    onClick={onAbrir}>
                    <FaUsers size={10} style={{ marginRight: 4 }} />
                    Liberada para {opcao.portados} participante{opcao.portados !== 1 ? 's' : ''}
                </button>
            )}
        </>
    );
}

/**
 * Escolha de participantes do evento para liberar ou retirar uma opção. Em `retirar` só
 * aparecem os liberados; quem escolheu a opção fica travado (não perde o que escolheu). Em
 * `portar`, quem já tem a opção aparece travado — liberar de novo não duplica.
 */
function ParticipantesModal({ modo, idEvento, eventoAereo, token, titulo, subtitulo, aviso, rotuloAcao = 'Liberar', processando, onConfirmar, onFechar }) {
    const [lista, setLista] = useState(null);
    const [busca, setBusca] = useState('');
    const [soComAereo, setSoComAereo] = useState(modo === 'portar');
    const [marcados, setMarcados] = useState(() => new Set());

    useEffect(() => {
        api({ method: 'get', action: '/eventoAereoPortacao/participantes', token, params: { evento: idEvento, eventoAereo: eventoAereo || undefined } })
            .then(res => setLista(res.data.data ?? []))
            .catch(err => {
                notify.apiError(err, 'Erro ao carregar os participantes');
                setLista([]);
            });
    }, [idEvento, eventoAereo, token]);

    const termo = busca.trim().toLowerCase();
    const bloqueado = (p) => (modo === 'portar' ? p.portado : p.escolheu);
    const motivoBloqueio = modo === 'portar' ? 'já tem esta opção' : 'escolheu esta opção, não sai';
    const visiveis = (lista ?? [])
        .filter(p => (modo === 'retirar' ? p.portado : (!soComAereo || p.aereo)))
        .filter(p => !termo || String(p.nome ?? '').toLowerCase().includes(termo) || String(p.cidade ?? '').toLowerCase().includes(termo));

    const alternar = (id) => setMarcados(prev => {
        const nova = new Set(prev);
        if (nova.has(id)) nova.delete(id); else nova.add(id);
        return nova;
    });
    const marcarVisiveis = () => setMarcados(prev => {
        const nova = new Set(prev);
        visiveis.filter(p => !bloqueado(p)).forEach(p => nova.add(p.id));
        return nova;
    });

    return (
        <ConfirmarAcaoModal aberto
            titulo={titulo} subtitulo={subtitulo}
            rotulo={`${modo === 'portar' ? rotuloAcao : 'Retirar'} (${marcados.size})`} rotuloFechar="Voltar"
            tom={modo === 'portar' ? 'primary' : 'danger'} icone={modo === 'portar' ? 'enviar' : 'danger'}
            confirmarPreso={marcados.size === 0} processando={processando}
            onConfirmar={() => onConfirmar([...marcados])} onFechar={onFechar}>
            {aviso && <p style={{ margin: '0 0 12px', lineHeight: 1.6 }}>{aviso}</p>}
            <div className="d-flex gap-2 flex-wrap align-items-center" style={{ marginBottom: 8 }}>
                <FinInput placeholder="Buscar por nome ou cidade" value={busca}
                    onChange={e => setBusca(e.target.value)} style={{ flex: 1, minWidth: 180 }} />
                {modo === 'portar' && (
                    <FinCheck label="Só quem tem aéreo" checked={soComAereo} onChange={e => setSoComAereo(e.target.checked)} />
                )}
            </div>
            <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: 6 }}>
                <span className="fin-pp__voo-info">
                    {lista === null ? 'Carregando...' : `${visiveis.length} participante${visiveis.length !== 1 ? 's' : ''}`}
                </span>
                <button type="button" className="fin-pp__linkbtn" onClick={marcarVisiveis} disabled={!visiveis.length}>
                    Marcar os da lista
                </button>
            </div>
            {/* Rolagem própria: evento grande tem centenas de participantes. */}
            <div className="d-flex flex-column gap-1" style={{ maxHeight: 320, overflowY: 'auto' }}>
                {visiveis.map(p => (
                    <label key={p.id} className={'fin-pp__check-row' + (bloqueado(p) ? ' is-disabled' : '')}>
                        <input type="checkbox" className="fin-pp__check" disabled={bloqueado(p)}
                            checked={marcados.has(p.id)} onChange={() => alternar(p.id)} />
                        <span className="fin-pp__check-label">
                            {p.nome}{p.cidade ? ` · ${p.cidade}` : ''}{bloqueado(p) ? ` · ${motivoBloqueio}` : ''}
                        </span>
                    </label>
                ))}
            </div>
        </ConfirmarAcaoModal>
    );
}

function Autoria({ opcao }) {
    if (!opcao) return <span className="fin-pp__cell-muted">—</span>;
    return (
        <div className="fin-pp__voo-info">
            <div>{opcao.criadoPor ? `Cadastro: ${opcao.criadoPor}` : 'Cadastro sem autor'}</div>
            {opcao.atualizadoPor && <div>Alterada por {opcao.atualizadoPor}</div>}
        </div>
    );
}

export default function CadastroEventoPortacaoAereo({ match }) {
    const idEvento = match.params.idEvento;
    const history = useHistory();
    const token = localStorage.getItem('token');

    const [previa, setPrevia] = useState(null);
    const [headerEvento, setHeaderEvento] = useState({});
    const [carregando, setCarregando] = useState(true);
    const [bloqueio, setBloqueio] = useState(null);
    // A aba vai na URL (?aba=noEvento): trocar de aba entra no histórico, e a seta do navegador
    // volta para a aba anterior.
    const location = useLocation();
    const abaPedida = new URLSearchParams(location.search).get('aba');
    const aba = ['noEvento', 'manuais'].includes(abaPedida) ? abaPedida : 'malhas';
    const setAba = (nova) => {
        if (nova !== aba) history.push({ search: nova === 'malhas' ? '' : '?aba=' + nova });
    };
    // Planilha de cada malha aprovada, lida da própria malha: { [id]: { opcoes, trechos } | { erro } }.
    const [planilhas, setPlanilhas] = useState({});
    const pedidasRef = useRef(new Set());
    // Seleção nas planilhas, por lado: { [id da opção da malha]: { ida, retorno } }. Guardada por
    // malha (`lerSelecaoDaMalha`): volta ao abrir a tela, e sobrevive à ida da alterada a Eventos.
    const [marcados, setMarcados] = useState({});
    const selecaoRestauradaRef = useRef(false);
    // Seleção da aba "No evento", por linha.
    const [selecionadas, setSelecionadas] = useState(() => new Set());
    const [abertas, setAbertas] = useState(() => new Set()); // linhas com a comparação aberta
    const [confirmacao, setConfirmacao] = useState(null);     // { tipo, ... }
    const [aplicando, setAplicando] = useState(false);

    const carregar = useCallback(() => {
        setCarregando(true);
        return api({ method: 'get', action: '/eventoAereoPortacao', token, params: { evento: idEvento } })
            .then(res => {
                setPrevia(res.data.data);
                setBloqueio(null);
                // A seleção guardada de cada malha, por cima do que já está marcado na tela.
                if (!selecaoRestauradaRef.current) {
                    setMarcados(prev => (res.data.data?.malhas ?? []).reduce((acc, m) => ({ ...acc, ...lerSelecaoDaMalha(m.id) }), prev));
                    selecaoRestauradaRef.current = true;
                }
            })
            .catch(err => {
                if (err?.response?.status === 403) {
                    setBloqueio(err.response.data?.error || 'Seu perfil não importa opções de aéreo.');
                    return;
                }
                notify.apiError(err, 'Erro ao carregar a importação');
            })
            .finally(() => setCarregando(false));
    }, [idEvento, token]);

    useEffect(() => {
        carregar();
        api({ method: 'get', action: '/eventoAereoCabecalho', token, params: { evento: idEvento } })
            .then(res => setHeaderEvento(res.data?.data ?? {}))
            .catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idEvento]);

    // A planilha de cada malha aprovada vem da própria malha — é o que a tela da malha mostra. Uma
    // leitura por malha: importar muda o evento, não a malha.
    const idsMalhas = (previa?.malhas ?? []).map(m => String(m.id)).join(',');
    useEffect(() => {
        if (!idsMalhas) return;
        idsMalhas.split(',').forEach(id => {
            if (pedidasRef.current.has(id)) return;
            pedidasRef.current.add(id);
            api({ method: 'get', action: '/eventoAereoMalhaOpcao', token, params: { malha: id } })
                .then(res => {
                    const d = res.data.data ?? {};
                    setPlanilhas(prev => ({
                        ...prev,
                        [id]: {
                            // `_id` é a chave da linha na planilha; a opção gravada já tem id próprio.
                            opcoes: numerarPorGrupo(d.opcoes ?? []).map(op => ({ ...op, _id: op._id ?? String(op.id) })),
                            trechos: d.trechos ?? [],
                        },
                    }));
                })
                .catch(err => {
                    setPlanilhas(prev => ({ ...prev, [id]: { erro: true } }));
                    notify.apiError(err, 'Erro ao carregar a planilha da malha');
                });
        });
    }, [idsMalhas, token]);

    // Guarda a seleção de cada malha da prévia. Só depois de restaurar: antes, a tela vazia apagaria
    // o que estava guardado.
    useEffect(() => {
        if (!selecaoRestauradaRef.current || !previa) return;
        (previa.malhas ?? []).forEach(m => guardarSelecaoDaMalha(m.id, Object.fromEntries(
            (m.opcoes ?? []).map(o => String(o.malhaOpcao)).filter(id => marcados[id]).map(id => [id, marcados[id]]))));
    }, [marcados, previa]);

    // Opção da prévia pelo id da opção da malha, de todas as malhas aprovadas.
    const infoPorOpcao = useMemo(() => {
        const mapa = new Map();
        (previa?.malhas ?? []).forEach(m => (m.opcoes ?? []).forEach(o => mapa.set(String(o.malhaOpcao), o)));
        return mapa;
    }, [previa]);

    // A coluna "Importar" de uma planilha: os lados de cada opção, marcados ou não.
    // Uma seleção por malha aprovada, todas sobre o mesmo `marcados`: o botão de importar do
    // rodapé leva tudo que foi marcado, em qualquer uma delas.
    const selecaoPara = (opcoesMalha) =>
        selecaoDeImportacao({ opcoes: opcoesMalha, infoPorOpcao, marcados, setMarcados });

    const acoesImportar = useMemo(() => acoesDeImportacao(marcados, infoPorOpcao), [marcados, infoPorOpcao]);
    const ladosMarcados = contarLadosMarcados(acoesImportar);

    // Malhas ALTERADAS com opção marcada: quem aceita a alteração é o RSVP, e ele aceita no
    // Importar — a confirmação de cada uma vai antes, e a Importação grava em seguida.
    const malhaDaOpcao = useMemo(() => {
        const mapa = new Map();
        (previa?.malhas ?? []).forEach(m => (m.opcoes ?? []).forEach(o => mapa.set(String(o.malhaOpcao), m)));
        return mapa;
    }, [previa]);
    const alteradasMarcadas = useMemo(() => [...new Map(acoesImportar
        .map(a => malhaDaOpcao.get(a.malhaOpcao))
        .filter(m => m?.situacao === 'alterada')
        .map(m => [String(m.id), m])).values()], [acoesImportar, malhaDaOpcao]);
    const alteradaSemConfirmar = alteradasMarcadas.find(m => m.confirmarAlteracao);
    const decidirMalhas = (malhas, acao) => malhas.reduce((p, m) => p.then(() =>
        api({ method: 'post', action: '/eventoMalhaAereaAprovacao', token, data: { malha: m.id, acao } })), Promise.resolve());
    // Com alterada marcada, a Importação só grava depois de a alteração de cada uma ser aprovada.
    const aplicarAprovando = (acoes, mensagem) => {
        if (!alteradasMarcadas.length) return aplicar(acoes, mensagem);
        setAplicando(true);
        decidirMalhas(alteradasMarcadas, 'confirmar')
            .then(() => aplicar(acoes, mensagem))
            .catch(err => {
                notify.apiError(err, 'Erro ao aprovar a alteração');
                setAplicando(false);
                setConfirmacao(null);
                carregar();
            });
    };
    // "Aprovar com Eventos": as alteradas vão para Eventos, e o resto importa agora. A seleção das
    // alteradas fica guardada para quando elas voltarem aprovadas.
    const mandarAlteradasParaEventos = () => {
        const ids = new Set(alteradasMarcadas.map(m => String(m.id)));
        const resto = acoesImportar.filter(a => !ids.has(String(malhaDaOpcao.get(a.malhaOpcao)?.id)));
        const manter = new Set(acoesImportar.filter(a => ids.has(String(malhaDaOpcao.get(a.malhaOpcao)?.id))).map(a => a.malhaOpcao));
        setAplicando(true);
        decidirMalhas(alteradasMarcadas, 'solicitarEventos')
            .then(() => {
                if (resto.length) return aplicar(resto, `${plural(resto.length, 'opção importada', 'opções importadas')} para o evento; as alteradas foram para Eventos`, manter);
                toast.success(alteradasMarcadas.length === 1 ? 'Malha enviada para a aprovação de Eventos' : 'Malhas enviadas para a aprovação de Eventos');
                setAplicando(false);
                setConfirmacao(null);
                return carregar();
            })
            .catch(err => {
                notify.apiError(err, 'Erro ao enviar para Eventos');
                setAplicando(false);
                setConfirmacao(null);
                carregar();
            });
    };
    const duplicatasMarcadas = acoesImportar.filter(a => infoPorOpcao.get(a.malhaOpcao)?.estado === 'duplicata').length;
    const atualizadasMarcadas = acoesImportar.filter(a => infoPorOpcao.get(a.malhaOpcao)?.eventoAereo).length;

    // Linhas da aba "No evento": opções importadas das malhas aprovadas e as importadas sem origem.
    // O nome de cada malha na tela: "Malha n", contando só as de solicitação, e a sugestão de voos do evento pelo nome.
    const nomesDasMalhas = useMemo(() => {
        let n = 0;
        return Object.fromEntries((previa?.malhas ?? []).map(m => [String(m.id), m.sugestao ? 'Sugestão de Voos' : `Malha ${++n}`]));
    }, [previa]);

    const linhasNoEvento = useMemo(() => {
        if (!previa) return [];
        const daMalha = (previa.malhas ?? []).flatMap((m, indice) => (m.opcoes ?? [])
            .filter(o => !!o.eventoAereo)
            .map(o => ({ chave: o.malhaOpcao, tipo: 'malha', malha: m, indiceMalha: indice, o })));
        const semOrigem = (previa.semOrigem ?? []).map(r => ({ chave: 'sem:' + r.id, tipo: 'semOrigem', r }));
        return [...daMalha, ...semOrigem];
    }, [previa]);

    const contagemAba = {
        malhas: previa?.malhas?.length ?? 0,
        noEvento: linhasNoEvento.length,
        manuais: previa?.manuais?.length ?? 0,
    };

    const selecionadasLinhas = linhasNoEvento.filter(l => selecionadas.has(l.chave));
    const paraRetirar = selecionadasLinhas.filter(podeRetirar);

    const alternar = (chave) => setSelecionadas(prev => {
        const nova = new Set(prev);
        if (nova.has(chave)) nova.delete(chave); else nova.add(chave);
        return nova;
    });

    const alternarTodasNoEvento = () => {
        const todas = linhasNoEvento.map(l => l.chave);
        const todasMarcadas = todas.length > 0 && todas.every(c => selecionadas.has(c));
        setSelecionadas(todasMarcadas ? new Set() : new Set(todas));
    };

    const alternarDetalhe = (chave) => setAbertas(prev => {
        const nova = new Set(prev);
        if (nova.has(chave)) nova.delete(chave); else nova.add(chave);
        return nova;
    });

    // `manter`: ids de opção da malha cuja seleção continua marcada (e guardada) depois de gravar.
    const aplicar = (acoes, mensagem, manter = null) => {
        setAplicando(true);
        api({ method: 'post', action: '/eventoAereoPortacao', token, data: { evento: idEvento, acoes } })
            .then(res => {
                setPrevia(res.data.data);
                setSelecionadas(new Set());
                setMarcados(prev => (manter ? Object.fromEntries(Object.entries(prev).filter(([id]) => manter.has(id))) : {}));
                toast.success(mensagem);
            })
            .catch(err => notify.apiError(err, 'Erro ao aplicar a importação'))
            .finally(() => {
                setAplicando(false);
                setConfirmacao(null);
            });
    };

    const plural = (n, um, varios) => `${n} ${n !== 1 ? varios : um}`;

    const confirmar = () => {
        if (!confirmacao) return;
        if (confirmacao.tipo === 'importar') {
            aplicarAprovando(acoesImportar, `${plural(acoesImportar.length, 'opção importada', 'opções importadas')} para o evento`);
        } else if (confirmacao.tipo === 'retirar') {
            aplicar(paraRetirar.map(l => ({ acao: 'retirar', malhaOpcao: noEvento(l).malhaOpcao ?? l.chave })),
                `${plural(paraRetirar.length, 'opção retirada', 'opções retiradas')} do evento`);
        }
    };

    // Importar e retirar dos participantes: a confirmação é o próprio modal de participantes,
    // que devolve os ids marcados.
    const confirmarParticipantes = (ids) => {
        const quantos = plural(ids.length, 'participante', 'participantes');
        if (confirmacao.tipo === 'importarParticipantes') {
            aplicarAprovando(acoesDeImportacao(marcados, infoPorOpcao, 'portarParticipantes').map(a => ({ ...a, participantes: ids })),
                `Importada para ${quantos}`);
        } else if (confirmacao.tipo === 'liberarParticipantes') {
            aplicar(selecionadasLinhas.map(l => ({ acao: 'portarParticipantes', eventoAereo: noEvento(l).id, participantes: ids })),
                `Liberada para ${quantos}`);
        } else if (confirmacao.tipo === 'liberarParticipantesOpcao') {
            aplicar([{ acao: 'portarParticipantes', eventoAereo: confirmacao.eventoAereo, participantes: ids }],
                `Liberada para ${quantos}`);
        } else if (confirmacao.tipo === 'retirarParticipantes') {
            aplicar([{ acao: 'retirarParticipantes', eventoAereo: confirmacao.eventoAereo, participantes: ids }],
                `Retirada de ${quantos}`);
        }
    };

    const irParaOpcoes = () => history.push('/cadastroEventoRsvpOpcaoAereo/' + idEvento);

    const totalNoEvento = (previa?.manuais?.length ?? 0) + linhasNoEvento.length;
    // Várias planilhas na mesma página: cada uma rola dentro de uma altura fixa.
    const alturaPlanilha = Math.max(320, Math.round(window.innerHeight * 0.62));

    const renderLinhaNoEvento = (linha) => {
        const evento = noEvento(linha);
        const daMalha = linha.tipo === 'malha';
        const estado = daMalha
            ? (ESTADO[linha.o.estado] ?? { rotulo: linha.o.estado, cor: 'is-muted', dica: '' })
            : { rotulo: 'Sem origem', cor: 'is-warn', dica: 'Veio de uma malha que não está mais aprovada, ou de uma opção que saiu dela.' };
        const diferencas = daMalha ? diferencasDe(linha.o) : [];
        const aberta = abertas.has(linha.chave);
        return (
            <React.Fragment key={linha.chave}>
                <tr className={selecionadas.has(linha.chave) ? 'is-selecionada' : undefined}>
                    <td className="fin-pp__tbl-check">
                        <input type="checkbox" className="fin-pp__check"
                            checked={selecionadas.has(linha.chave)} disabled={aplicando}
                            onChange={() => alternar(linha.chave)} />
                    </td>
                    <td className="fin-pp__cell-strong">
                        {daMalha ? `${nomesDasMalhas[String(linha.malha.id)] ?? `Malha ${linha.indiceMalha + 1}`} · Opção ${linha.o.opcao ?? '—'}` : '—'}
                    </td>
                    {/* O que o participante vê: os campos da opção do evento. */}
                    <td>{evento.campos.aeroportoOrigem || <span className="fin-pp__cell-muted">—</span>}</td>
                    <td><Voo campos={evento.campos} lado="ida" /></td>
                    <td><Voo campos={evento.campos} lado="retorno" /></td>
                    <td>
                        <div className="d-flex flex-column align-items-start gap-1">
                            <span className={'fin-pp__pill ' + estado.cor} title={estado.dica}>{estado.rotulo}</span>
                            {daMalha && linha.o.estado === 'divergente' && diferencas.length > 0 && (
                                <button type="button" className="fin-pp__linkbtn" onClick={() => alternarDetalhe(linha.chave)}>
                                    {aberta ? 'Ocultar diferenças' : `Ver ${plural(diferencas.length, 'diferença', 'diferenças')}`}
                                </button>
                            )}
                            <Portados opcao={evento} disabled={aplicando}
                                onAbrir={() => setConfirmacao({ tipo: 'retirarParticipantes', eventoAereo: evento.id })} />
                        </div>
                    </td>
                    <td><Escolhas opcao={evento} /></td>
                    <td><Autoria opcao={evento} /></td>
                </tr>
                {aberta && daMalha && diferencas.length > 0 && (
                    <tr className="is-detalhe">
                        <td></td>
                        <td colSpan={7}>
                            <div className="fin-pp__difs">
                                <span className="fin-pp__difs-head">Campo</span>
                                <span className="fin-pp__difs-head">No evento (o participante vê)</span>
                                <span className="fin-pp__difs-head">Na malha</span>
                                {diferencas.map(campo => (
                                    <React.Fragment key={campo}>
                                        <span>{ROTULO_CAMPO[campo] ?? campo}</span>
                                        <span>{evento.campos[campo] || '—'}</span>
                                        <span>{linha.o.campos[campo] || '—'}</span>
                                    </React.Fragment>
                                ))}
                            </div>
                        </td>
                    </tr>
                )}
            </React.Fragment>
        );
    };

    return (
        <ErpTemplate>
            <div className="main-content container-fluid fin-pp">
                <CabecalhoEventoAereo
                    titulo="Importação das Opções de Aéreo"
                    nomeEvento={headerEvento.nomeEvento}
                    cliente={headerEvento.cliente}
                    solicitante={headerEvento.solicitante}
                    dataEvento={headerEvento.dataEvento}
                    localEvento={headerEvento.localEvento}
                    voosSolicitados={previa ? `${totalNoEvento} ${totalNoEvento !== 1 ? 'opções' : 'opção'} no evento` : ''}
                    // Sem `tktMedio`: a Importação é do evento inteiro, e o teto é de cada solicitação.
                    atalhos={[{
                        rotulo: 'Solicitações',
                        titulo: 'Painel de Solicitações deste evento.',
                        to: '/cadastroEventoSolicitacoesAereo/' + idEvento,
                    }]}
                />

                <div className="fin-pp__head">
                    <div>
                        <h1 className="fin-pp__title">Importação</h1>
                        <p className="fin-pp__subtitle">
                            Marque na planilha de cada malha a ida e a volta que vão para as opções do evento. Dá para importar e retirar quantas vezes precisar.
                        </p>
                    </div>
                    <div className="fin-pp__actions">
                        {/* Uma escolha de UM entre N, e não três botões soltos: a marcação diz
                            isso. Aqui a escolha troca o painel abaixo, que é o padrão de ABA
                            (tablist/tab/aria-selected) — é ele, e não o de rádio, que faz o leitor
                            de tela anunciar "aba 2 de 3, selecionada" e as setas andarem entre
                            elas. Visualmente nada muda. */}
                        {previa && (
                            <div className="fin-pp__seg" role="tablist" aria-label="Abas da importação">
                                {ABAS.map(a => (
                                    <button key={a.key} type="button" role="tab" aria-selected={aba === a.key}
                                        className={'fin-pp__seg-btn' + (aba === a.key ? ' is-active' : '')}
                                        onClick={() => setAba(a.key)}>
                                        {a.label}
                                        <span className="fin-pp__seg-count">{contagemAba[a.key]}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                        <button type="button" className="fin-pp__btn fin-pp__btn--pill" onClick={irParaOpcoes}
                            title="Abre a tela de opções de aéreo, onde se cadastram e editam as opções manuais.">
                            <FaExternalLinkAlt size={11} />
                            Opções de aéreo
                        </button>
                    </div>
                </div>

                {bloqueio && (
                    <div className="fin-pp__card mb-3" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 16 }}>
                        <span className="fin-pp__cphead-ic"><FaLock size={14} /></span>
                        <div className="fin-pp__cphead-sub" style={{ margin: 0 }}>{bloqueio}</div>
                    </div>
                )}

                {!bloqueio && carregando && !previa && (
                    <div className="fin-pp__card fin-pp__empty mb-3">Carregando...</div>
                )}

                {/* ── Malhas: a planilha de cada uma, com a coluna "Importar" ── */}

                {previa && aba === 'malhas' && (previa.malhas ?? []).length === 0 && (
                    <div className="fin-pp__card fin-pp__empty mb-3">
                        Nenhuma malha para importar neste evento. Entram a aprovada e a que está em alteração — nesta a aprovação de Eventos continua de pé. Malha de solicitação cancelada não entra.
                    </div>
                )}

                {previa && aba === 'malhas' && (previa.malhas ?? []).map((m, indice) => {
                    const planilha = planilhas[String(m.id)];
                    const nome = nomesDasMalhas[String(m.id)] ?? `Malha ${indice + 1}`;
                    const info = m.sugestao ? 'Enviada pelo Aéreo com a emissão' : [
                        `Aprovada em ${dataCurta(m.aprovadaEm)}${m.aprovadaPor ? ' por ' + m.aprovadaPor : ''}`,
                        m.agencia && `Agência ${m.agencia}`,
                    ].filter(Boolean).join(' · ');
                    if (!planilha || planilha.erro) {
                        return (
                            <div key={m.id} className="fin-pp__card fin-pp__empty mb-3">
                                {`${nome} · ${info} — `}
                                {planilha?.erro ? 'não foi possível carregar a planilha.' : 'carregando a planilha...'}
                            </div>
                        );
                    }
                    return (
                        <PlanilhaMalhaAerea
                            key={m.id}
                            titulo={nome}
                            opcoes={planilha.opcoes}
                            trechos={planilha.trechos}
                            selecao={selecaoPara(planilha.opcoes)}
                            alturaMaxima={alturaPlanilha}
                            acoes={(
                                <>
                                    <span style={{ color: '#7b8ba7', fontSize: 12 }}>{info}</span>
                                    <button type="button" className="fin-pp__btn fin-pp__btn--pill fin-pp__btn--sm"
                                        title="Abre a malha, com o histórico de uploads e os trechos."
                                        onClick={() => history.push('/cadastroEventoImportarMalhaAereo/' + m.id)}>
                                        <FaExternalLinkAlt size={10} />
                                        Abrir malha
                                    </button>
                                </>
                            )}
                        />
                    );
                })}

                {/* ── No evento: o que já foi importado, para retirar e liberar a participantes ── */}

                {previa && aba === 'noEvento' && (
                    <div className="fin-pp__card mb-3" style={{ overflow: 'hidden' }}>
                        <div className="fin-pp__trecho-head">
                            <span className="fin-pp__cphead-ic"><FaPlaneDeparture size={14} /></span>
                            <div className="fin-pp__trecho-txt" style={{ flex: 1 }}>
                                <span className="fin-pp__cphead-title">Importadas no evento</span>
                                <span className="fin-pp__cphead-sub">
                                    Vieram das malhas. "Sem origem" é a que veio de uma malha que não está mais aprovada, ou de uma opção que saiu dela: continua no evento até alguém retirar.
                                </span>
                            </div>
                        </div>
                        <div className="fin-pp__tblwrap">
                            <table className="fin-pp__tbl" style={{ minWidth: 1100, tableLayout: 'auto' }}>
                                <thead>
                                    <tr>
                                        <th className="fin-pp__tbl-check">
                                            <input type="checkbox" className="fin-pp__check"
                                                checked={linhasNoEvento.length > 0 && linhasNoEvento.every(l => selecionadas.has(l.chave))}
                                                disabled={!linhasNoEvento.length || aplicando}
                                                title="Marca ou desmarca todas"
                                                onChange={alternarTodasNoEvento} />
                                        </th>
                                        <th>Origem</th>
                                        <th>Cidade</th>
                                        <th>Ida</th>
                                        <th>Retorno</th>
                                        <th>Situação</th>
                                        <th>Escolhas</th>
                                        <th>Autoria</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {linhasNoEvento.length === 0
                                        ? <tr><td colSpan={8} className="fin-pp__empty">Nenhuma opção importada no evento.</td></tr>
                                        : linhasNoEvento.map(renderLinhaNoEvento)}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* ── Opções manuais: só leitura e liberar a participantes ── */}

                {previa && aba === 'manuais' && (
                    <div className="fin-pp__card mb-3" style={{ overflow: 'hidden' }}>
                        <div className="fin-pp__trecho-head">
                            <span className="fin-pp__cphead-ic"><FaPlaneDeparture size={14} /></span>
                            <div className="fin-pp__trecho-txt" style={{ flex: 1 }}>
                                <span className="fin-pp__cphead-title">Opções manuais do evento</span>
                                <span className="fin-pp__cphead-sub">
                                    Cadastradas na tela de opções do evento, no cadastro do participante ou por planilha. Não se ligam a uma malha; editar é na tela de opções do evento ou do participante.
                                </span>
                            </div>
                        </div>
                        <div className="fin-pp__tblwrap">
                            <table className="fin-pp__tbl" style={{ minWidth: 980, tableLayout: 'auto' }}>
                                <thead>
                                    <tr>
                                        <th>Cidade</th>
                                        <th>Ida</th>
                                        <th>Retorno</th>
                                        <th>Escolhas</th>
                                        <th>Autoria</th>
                                        <th style={{ width: 1 }}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(previa.manuais ?? []).length === 0
                                        ? <tr><td colSpan={6} className="fin-pp__empty">Nenhuma opção manual.</td></tr>
                                        : previa.manuais.map(d => (
                                            <tr key={d.id}>
                                                <td>{d.campos.aeroportoOrigem || '—'}</td>
                                                <td><Voo campos={d.campos} lado="ida" /></td>
                                                <td><Voo campos={d.campos} lado="retorno" /></td>
                                                <td><Escolhas opcao={d} /></td>
                                                <td><Autoria opcao={d} /></td>
                                                <td>
                                                    <div className="d-flex flex-column align-items-end gap-1">
                                                        <button type="button" className="fin-pp__btn fin-pp__btn--sm"
                                                            disabled={aplicando}
                                                            title="Mostra esta opção também para participantes escolhidos, de qualquer cidade."
                                                            onClick={() => setConfirmacao({ tipo: 'liberarParticipantesOpcao', eventoAereo: d.id })}>
                                                            <FaUsers size={11} />
                                                            Liberar para participantes
                                                        </button>
                                                        <Portados opcao={d} disabled={aplicando}
                                                            onAbrir={() => setConfirmacao({ tipo: 'retirarParticipantes', eventoAereo: d.id })} />
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {previa && aba === 'malhas' && (previa.malhas ?? []).length > 0 && (
                    <div className="row fixoRodape">
                        <div className="col-12 d-flex align-items-center justify-content-end gap-3 flex-wrap">
                            <span className="fin-pp__voo-info">
                                {ladosMarcados === 0
                                    ? 'Marque na planilha'
                                    : `${plural(ladosMarcados, 'lado marcado', 'lados marcados')} em ${plural(acoesImportar.length, 'opção', 'opções')}`}
                            </span>
                            {ladosMarcados > 0 && (
                                <button type="button" className="fin-pp__btn" disabled={aplicando}
                                    onClick={() => setMarcados({})}>
                                    Limpar
                                </button>
                            )}
                            <button type="button" className="fin-pp__btn fin-pp__btn--tall"
                                disabled={aplicando || acoesImportar.length === 0}
                                title="Importa as marcadas só para participantes escolhidos, de qualquer cidade."
                                onClick={() => setConfirmacao({ tipo: 'importarParticipantes' })}>
                                <FaUsers size={12} />
                                Importar para participantes ({acoesImportar.length})
                            </button>
                            <button type="button" className="fin-pp__btn fin-pp__btn--tall fin-pp__btn--primary"
                                disabled={aplicando || acoesImportar.length === 0}
                                title="Leva os lados marcados para as opções do evento. Importar de novo atualiza sem duplicar."
                                onClick={() => setConfirmacao({ tipo: 'importar' })}>
                                Importar para o evento ({acoesImportar.length})
                            </button>
                        </div>
                    </div>
                )}

                {previa && aba === 'noEvento' && (
                    <div className="row fixoRodape">
                        <div className="col-12 d-flex align-items-center justify-content-end gap-3 flex-wrap">
                            <span className="fin-pp__voo-info">
                                {selecionadas.size === 0
                                    ? 'Marque as opções para retirar ou liberar a participantes.'
                                    : plural(selecionadas.size, 'marcada', 'marcadas')}
                            </span>
                            {selecionadas.size > 0 && (
                                <button type="button" className="fin-pp__btn" disabled={aplicando}
                                    onClick={() => setSelecionadas(new Set())}>
                                    Limpar
                                </button>
                            )}
                            <button type="button" className="fin-pp__btn fin-pp__btn--tall fin-pp__btn--danger-ghost"
                                disabled={aplicando || paraRetirar.length === 0}
                                title="Tira do evento as marcadas. Opção escolhida por participante não sai."
                                onClick={() => setConfirmacao({ tipo: 'retirar' })}>
                                Retirar do evento ({paraRetirar.length})
                            </button>
                            <button type="button" className="fin-pp__btn fin-pp__btn--tall"
                                disabled={aplicando || selecionadasLinhas.length === 0}
                                title="Mostra as marcadas também para participantes escolhidos, de qualquer cidade."
                                onClick={() => setConfirmacao({ tipo: 'liberarParticipantes' })}>
                                <FaUsers size={12} />
                                Liberar para participantes ({selecionadasLinhas.length})
                            </button>
                        </div>
                    </div>
                )}

                {confirmacao?.tipo === 'importar' && (alteradasMarcadas.length > 0 ? (
                    // Malha alterada marcada: o Importar é onde o RSVP aceita a alteração.
                    <ConfirmarAcaoModal aberto
                        titulo="Aprovar a alteração e importar?"
                        subtitulo={`${alteradasMarcadas.length === 1 ? 'Uma das malhas marcadas foi alterada' : `${alteradasMarcadas.length} das malhas marcadas foram alteradas`} pelo Aéreo. Importar aprova a alteração: a aprovação que Eventos já deu continua valendo.`}
                        aviso={alteradaSemConfirmar ? alteradaSemConfirmar.confirmarAlteracao : null}
                        confirmarPreso={!!alteradaSemConfirmar}
                        rotulo="Aprovar e importar" rotuloFechar="Voltar" tom="ok" icone="ok"
                        alternativa={{
                            rotulo: 'Aprovar com Eventos',
                            tom: 'primary',
                            titulo: 'As alteradas vão para Eventos aprovar de novo, e o resto importa agora. A seleção delas fica guardada.',
                            onConfirmar: mandarAlteradasParaEventos,
                        }}
                        processando={aplicando} onConfirmar={confirmar} onFechar={() => setConfirmacao(null)} />
                ) : (
                    <ConfirmarAcaoModal aberto
                        titulo={`Importar ${plural(acoesImportar.length, 'opção', 'opções')} para o evento?`}
                        subtitulo="Só os lados marcados entram. Os participantes da cidade de cada opção passam a vê-la. Importar de novo atualiza a mesma opção, sem duplicar."
                        aviso={[
                            duplicatasMarcadas > 0 && `${duplicatasMarcadas} ${duplicatasMarcadas !== 1 ? 'têm' : 'tem'} opção manual com o mesmo voo: importar cria outra ao lado.`,
                            atualizadasMarcadas > 0 && `${atualizadasMarcadas} já ${atualizadasMarcadas !== 1 ? 'estão no evento e são atualizadas' : 'está no evento e é atualizada'} com a malha.`,
                        ].filter(Boolean).join(' ') || null}
                        rotulo="Importar" tom="primary" icone="enviar"
                        processando={aplicando} onConfirmar={confirmar} onFechar={() => setConfirmacao(null)} />
                ))}

                {confirmacao?.tipo === 'retirar' && (
                    <ConfirmarAcaoModal aberto
                        titulo={`Retirar ${plural(paraRetirar.length, 'opção', 'opções')} do evento?`}
                        subtitulo="Saem das opções do evento todo. A que tiver participantes liberados continua só para eles. A opção da malha continua lá, e dá para importar de novo."
                        rotulo="Retirar" rotuloFechar="Voltar" tom="danger" icone="danger"
                        processando={aplicando} onConfirmar={confirmar} onFechar={() => setConfirmacao(null)} />
                )}

                {confirmacao?.tipo === 'importarParticipantes' && (
                    <ParticipantesModal modo="portar" idEvento={idEvento} token={token} rotuloAcao="Importar"
                        titulo={`Importar ${plural(acoesImportar.length, 'opção', 'opções')} para participantes?`}
                        subtitulo="Os marcados passam a ver as opções, de qualquer cidade. A que ainda não está no evento entra só para eles, com os lados marcados; a que já está só ganha os participantes. Importar de novo não duplica."
                        aviso={[
                            alteradasMarcadas.length > 0 && (alteradaSemConfirmar
                                ? alteradaSemConfirmar.confirmarAlteracao
                                : `${alteradasMarcadas.length === 1 ? 'Uma malha marcada foi alterada' : `${alteradasMarcadas.length} malhas marcadas foram alteradas`} pelo Aéreo: importar aprova a alteração.`),
                            duplicatasMarcadas > 0 && `${duplicatasMarcadas} ${duplicatasMarcadas !== 1 ? 'têm' : 'tem'} opção manual com o mesmo voo: importar cria outra ao lado.`,
                        ].filter(Boolean).join(' ') || null}
                        processando={aplicando} onConfirmar={confirmarParticipantes} onFechar={() => setConfirmacao(null)} />
                )}

                {confirmacao?.tipo === 'liberarParticipantes' && (
                    <ParticipantesModal modo="portar" idEvento={idEvento} token={token}
                        titulo={`Liberar ${plural(selecionadasLinhas.length, 'opção', 'opções')} para participantes?`}
                        subtitulo="Os marcados passam a ver as opções, de qualquer cidade. Elas continuam para o evento todo. Liberar de novo não duplica."
                        processando={aplicando} onConfirmar={confirmarParticipantes} onFechar={() => setConfirmacao(null)} />
                )}

                {confirmacao?.tipo === 'liberarParticipantesOpcao' && (
                    <ParticipantesModal modo="portar" idEvento={idEvento} eventoAereo={confirmacao.eventoAereo} token={token}
                        titulo="Liberar a opção para participantes?"
                        subtitulo="Os marcados passam a ver a opção, de qualquer cidade. Ela continua para o evento todo."
                        processando={aplicando} onConfirmar={confirmarParticipantes} onFechar={() => setConfirmacao(null)} />
                )}

                {confirmacao?.tipo === 'retirarParticipantes' && (
                    <ParticipantesModal modo="retirar" idEvento={idEvento} eventoAereo={confirmacao.eventoAereo} token={token}
                        titulo="Retirar a opção de participantes?"
                        subtitulo="Os marcados deixam de ver a opção, a não ser que ela seja do evento todo e da cidade deles. Quem escolheu a opção não sai. Sem nenhum participante, a opção que era só deles sai do evento."
                        processando={aplicando} onConfirmar={confirmarParticipantes} onFechar={() => setConfirmacao(null)} />
                )}
            </div>
        </ErpTemplate>
    );
}
